import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plane, Calendar, MapPin } from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@tabipla/ui/components/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui/components/select";
import { toast } from "sonner";
import {
  useUserTrips,
  useTripDetail,
  useAddMekkeSpotToTrip,
} from "../../../application/hooks/useIntegration";

interface AddToTripDialogProps {
  spotId: string;
  spotName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddToTripDialog({ spotId, spotName, open, onOpenChange }: AddToTripDialogProps) {
  const { t } = useTranslation();
  const { data: trips, isLoading: isLoadingTrips } = useUserTrips(open);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [selectedItineraryId, setSelectedItineraryId] = useState<string | null>(null);
  const { data: tripDetail, isLoading: isLoadingDetail } = useTripDetail(selectedTripId);
  const addToTripMutation = useAddMekkeSpotToTrip();

  // Reset selection when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTripId(null);
      setSelectedItineraryId(null);
    }
  }, [open]);

  // Reset itinerary selection when trip changes
  useEffect(() => {
    setSelectedItineraryId(null);
  }, [selectedTripId]);

  const handleAdd = async () => {
    if (!selectedTripId || !selectedItineraryId) return;

    try {
      await addToTripMutation.mutateAsync({
        mekkeSpotId: spotId,
        tripId: selectedTripId,
        itineraryId: selectedItineraryId,
      });
      toast.success(t("spots.addedToTabitto"));
      onOpenChange(false);
    } catch {
      toast.error(t("common.error"));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
    });
  };

  const hasTrips = trips && trips.length > 0;
  const hasItineraries = tripDetail && tripDetail.itineraries.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-blue-500" />
            {t("spots.addToTabitto")}
          </DialogTitle>
          <DialogDescription>
            {t("spots.addToTabittoDescription", { name: spotName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Trip Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("spots.selectTrip")}</label>
            {isLoadingTrips ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !hasTrips ? (
              <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                {t("spots.noTrips")}
              </div>
            ) : (
              <Select
                value={selectedTripId ?? ""}
                onValueChange={(value) => setSelectedTripId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("spots.selectTripPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {trips.map((trip) => (
                    <SelectItem key={trip.id} value={trip.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{trip.title}</span>
                        {trip.startDate && (
                          <span className="text-xs text-muted-foreground">
                            ({formatDate(trip.startDate)})
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Itinerary Selection */}
          {selectedTripId && (
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("spots.selectDay")}</label>
              {isLoadingDetail ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : !hasItineraries ? (
                <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                  {t("spots.noItineraries")}
                </div>
              ) : (
                <Select
                  value={selectedItineraryId ?? ""}
                  onValueChange={(value) => setSelectedItineraryId(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("spots.selectDayPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tripDetail.itineraries.map((itinerary) => (
                      <SelectItem key={itinerary.id} value={itinerary.id}>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>{t("spots.day", { number: itinerary.dayNumber })}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatDate(itinerary.date)})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!selectedTripId || !selectedItineraryId || addToTripMutation.isPending}
            className="gap-2"
          >
            {addToTripMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("spots.addToTrip")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
